import { useMemo } from "react";
import type { HarmonicAnalysis, HarmonicChannel } from "@/lib/types";

// System-level PQ summary: THD averages, voltage unbalance, symmetrical
// components, neutral current — plus inrush characterisation with per-phase
// peak breakdown. Renders whatever the backend could compute; a
// system_aggregates_skipped_reason is surfaced rather than hidden.

function fmt(v: number | null | undefined, digits = 2, unit = ""): string {
  if (v == null) return "—";
  return `${v.toFixed(digits)}${unit}`;
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm border-b last:border-0">
      <dt className="text-muted-foreground">
        {label}
        {hint && <span className="ml-1 text-[10px] text-muted-foreground/70">{hint}</span>}
      </dt>
      <dd className="text-right font-mono text-xs">{value}</dd>
    </div>
  );
}

export function SystemSummaryPanel({ analysis }: { analysis: HarmonicAnalysis }) {
  const sys = analysis.system;
  // Per-phase current channels that have a computed inrush peak (A/B/C order).
  const iPhases = useMemo(() => {
    const out: HarmonicChannel[] = [];
    for (const role of ["I_A", "I_B", "I_C"]) {
      const ch = analysis.channels.find(
        (c) => c.channel_role === role && c.peak_inst_current != null
      );
      if (ch) out.push(ch);
    }
    return out;
  }, [analysis.channels]);

  // Worst-phase channel — drives the timing + fundamentals block beneath the
  // per-phase peaks.
  const worst = useMemo(() => {
    if (iPhases.length === 0) return null;
    return iPhases.reduce((a, b) =>
      (b.peak_inst_current ?? 0) > (a.peak_inst_current ?? 0) ? b : a
    );
  }, [iPhases]);

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div>
        <h3 className="text-sm font-medium mb-1">System aggregates</h3>
        {sys.system_aggregates_skipped_reason && (
          <p className="text-xs text-amber-400 mb-1">
            {sys.system_aggregates_skipped_reason}
          </p>
        )}
        <dl>
          <Stat label="Voltage THD" hint="3-phase avg" value={fmt(sys.v_thd_avg_pct, 2, "%")} />
          <Stat label="Current THD" hint="3-phase avg" value={fmt(sys.i_thd_avg_pct, 2, "%")} />
          <Stat
            label="Voltage unbalance"
            hint="neg/pos seq"
            value={fmt(sys.v_unbalance_pct, 2, "%")}
          />
          <Stat label="Seq — positive" value={fmt(sys.seq_v_pos, 2)} />
          <Stat label="Seq — negative" value={fmt(sys.seq_v_neg, 2)} />
          <Stat label="Seq — zero" value={fmt(sys.seq_v_zero, 2)} />
          <Stat
            label="Neutral current"
            hint="measured"
            value={fmt(sys.neutral_i_measured, 2, " A")}
          />
          <Stat
            label="Neutral current"
            hint="calc Σ phases"
            value={fmt(sys.neutral_i_calculated, 2, " A")}
          />
        </dl>
      </div>

      <div>
        <h3 className="text-sm font-medium mb-1">Inrush</h3>
        {worst ? (
          <dl>
            {iPhases.map((ch) => (
              <Stat
                key={ch.channel_role}
                label={`Peak — ${ch.channel_role.replace("I_", "")}`}
                hint={ch === worst && iPhases.length > 1 ? "worst" : undefined}
                value={fmt(ch.peak_inst_current, 1, " A")}
              />
            ))}
            <Stat
              label="Time to peak"
              hint={iPhases.length > 1 ? worst.channel_role : undefined}
              value={fmt(worst.time_to_peak_s, 4, " s")}
            />
            <Stat
              label="Decay constant τ"
              value={worst.decay_tau_s != null ? fmt(worst.decay_tau_s, 4, " s") : "—"}
            />
            <Stat
              label="Fundamental — first cycle"
              value={fmt(worst.i_fund_first_cycle, 1, " A")}
            />
            <Stat
              label="Fundamental — steady state"
              value={fmt(worst.i_fund_steady_state, 1, " A")}
            />
            <Stat
              label="Inrush factor"
              hint="first / steady"
              value={
                worst.i_fund_first_cycle != null &&
                worst.i_fund_steady_state &&
                worst.i_fund_steady_state > 0
                  ? `${(worst.i_fund_first_cycle / worst.i_fund_steady_state).toFixed(2)}×`
                  : "—"
              }
            />
          </dl>
        ) : (
          <p className="text-xs text-muted-foreground py-2">
            No trigger detected — inrush characterisation not applicable.
          </p>
        )}
      </div>
    </div>
  );
}
